// src/components/ProgramInviteGeneratorModal.js

import React, { useRef } from 'react'; // Added useRef
import { useQuery } from '@tanstack/react-query';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, CircularProgress, Typography, Grid, Paper, IconButton, Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QRCode from 'qrcode.react';
import DownloadIcon from '@mui/icons-material/Download'; // Added DownloadIcon
import PrintIcon from '@mui/icons-material/Print'; // Added PrintIcon
import { useAuth } from '../contexts/AuthContext'; // Corrected import path if needed
import authFetch from '../utils/authFetch';
import logger from '../utils/logger';

// API function to fetch programs for the tenant
const fetchTenantPrograms = (tenantId) => {
    if (!tenantId) return Promise.resolve([]); // Return empty array if no tenantId
    // Assuming authFetch handles tenant context or API endpoint filters by tenant
    return authFetch('/api/programs');
};

const ProgramInviteGeneratorModal = ({ open, onClose }) => {
    // Get user and selectedTenant from useAuth
    const { user, selectedTenant } = useAuth();
    const tenantId = selectedTenant?.tenant_id;

    // Fetch programs using React Query
    const { data: programs = [], isLoading, isError } = useQuery({
        queryKey: ['tenantPrograms', tenantId],
        queryFn: () => fetchTenantPrograms(tenantId),
        enabled: open && !!tenantId, // Only run query when modal is open and tenantId is available
    });

    // Determine the base URL for the patient portal
    // Make sure this env variable is set in your .env file (e.g., REACT_APP_PATIENT_PORTAL_BASE_URL=https://e1p.medsai.us)
    const patientPortalOrigin = process.env.REACT_APP_PATIENT_PORTAL_BASE_URL || 'https://app.medsai.us';
    logger.info(`ProgramInviteGeneratorModal: Using patientPortalOrigin: ${patientPortalOrigin}`);

    // --- Helper Function to Download QR Code ---
    const downloadQRCode = (programName, qrCodeId) => {
        const canvas = document.getElementById(qrCodeId);
        if (canvas) {
            const pngUrl = canvas
                .toDataURL('image/png')
                .replace('image/png', 'image/octet-stream'); // Force download
            let downloadLink = document.createElement('a');
            downloadLink.href = pngUrl;
            // Sanitize program name for filename
            const safeProgramName = programName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
            downloadLink.download = `${safeProgramName}_enroll_qr.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } else {
            logger.error(`Could not find QR Code canvas with ID: ${qrCodeId}`);
        }
    };

    // --- Helper Function to Print QR Code Card ---
    const printQRCodeCard = (programName, qrCodeId, enrollmentUrl) => {
        const canvas = document.getElementById(qrCodeId);
        if (!canvas) {
            logger.error(`Could not find QR Code canvas with ID: ${qrCodeId} for printing.`);
            return;
        }
        const qrCodeDataUrl = canvas.toDataURL('image/png');

        const printWindow = window.open('', '_blank', 'height=600,width=800');
        if (printWindow) {
            printWindow.document.write('<html><head><title>Print QR Code Card</title>');
            // Add some basic print styling
            printWindow.document.write(`
                <style>
                    body { font-family: sans-serif; text-align: center; padding: 20px; }
                    img { max-width: 80%; height: auto; margin-bottom: 20px; display: block; margin-left: auto; margin-right: auto; }
                    h1 { margin-bottom: 15px; font-size: 1.8em; }
                    /* Style for the instruction note */
                    .instruction-note { font-size: 0.9em; color: #333; margin-bottom: 20px; border: 1px dashed #ccc; padding: 10px; background-color: #f9f9f9; }
                    .link-text { word-break: break-all; font-size: 0.8em; color: #555; margin-top: 15px; }
                    @media print {
                       img { max-width: 400px; /* Adjust size for print */ }
                       body { margin: 0.5in; /* Standard print margin */ }
                       h1 { font-size: 24pt; }
                       .instruction-note { font-size: 10pt; border: none; background-color: transparent; padding: 0; }
                       .link-text { font-size: 9pt; }
                       /* Hide buttons in print view */
                       button, a { display: none !important; }
                    }
                </style>
            `);
            printWindow.document.write('</head><body>');
            printWindow.document.write(`<h1>${programName}</h1>`);
            printWindow.document.write(`<img src="${qrCodeDataUrl}" alt="QR Code for ${programName}" />`);
            // Update instruction note for the new flow
            printWindow.document.write(`<p class="instruction-note">Scan to open your personalized enrollment page. On the page, you can save your Care Team's contact.</p>`);
            printWindow.document.write(`<p class="link-text">Enrollment Link: ${enrollmentUrl}</p>`); // Show enrollment link text
            printWindow.document.write('</body></html>');
            printWindow.document.close(); // Important for some browsers
            printWindow.focus(); // Important for some browsers
            // Delay print slightly to ensure content is rendered
            setTimeout(() => {
                try {
                    printWindow.print();
                    printWindow.close();
                } catch (e) {
                    logger.error("Error during print:", e);
                    // Optionally close window even if print fails/is cancelled
                    printWindow.close();
                }
            }, 250);
        } else {
            logger.error("Could not open print window. Check browser pop-up blocker settings.");
        }
    };

    // --- REMOVED createVCardData function as it's no longer used for QR code generation ---

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth> {/* Changed maxWidth to lg */}
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Program Enrollment QR Codes
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {isLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading programs...</Typography>
                    </Box>
                )}
                {!isLoading && !isError && programs.length > 0 && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
                            Patients scan these QR codes to be taken directly to their personalized enrollment page. Once there, they can choose an account and save the Care Team's contact information. Use the buttons below each card to download the QR code image or print the card for display.
                        </Typography>
                        <Grid container spacing={3} sx={{ p: 2 }}>
                            {/* Filter for active programs AND SORT Alphabetically */}
                            {programs
                                .filter(p => p.is_active)
                                .sort((a, b) => a.name.localeCompare(b.name)) // Sort alphabetically by name
                                .map((program) => {
                                    // Generate the direct enrollment URL
                                    const enrollmentUrl = `${patientPortalOrigin}/enroll?tenantId=${tenantId}&programId=${program.id}`;
                                    // Unique ID for each QR canvas
                                    const qrCodeId = `qr-code-${program.id}`;

                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={program.id}>
                                            <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                <Typography variant="h6" gutterBottom>{program.name}</Typography>
                                                <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                                                    {/* Value for QR Code is now the direct Enrollment URL */}
                                                    <QRCode id={qrCodeId} value={enrollmentUrl} size={180} />
                                                </Box>
                                                {/* Display enrollment link text for reference */}
                                                <Link href={enrollmentUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '0.75rem', wordBreak: 'break-all', mt: 1, color: 'text.secondary' }}>
                                                    Enrollment Link
                                                </Link>

                                                {/* Download and Print Buttons */}
                                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<DownloadIcon />}
                                                        onClick={() => downloadQRCode(program.name, qrCodeId)}
                                                        aria-label={`Download QR Code for ${program.name}`}
                                                    >
                                                        QR Code
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<PrintIcon />}
                                                        onClick={() => printQRCodeCard(program.name, qrCodeId, enrollmentUrl)}
                                                        aria-label={`Print Card for ${program.name}`}
                                                    >
                                                        Print Card
                                                    </Button>
                                                </Box>
                                            </Paper>
                                        </Grid>
                                    );
                                })}
                        </Grid>
                    </>
                )}
                {!isLoading && !isError && programs.length === 0 && (
                     <Typography color="text.secondary" sx={{p: 3, textAlign: 'center'}}>No active programs found for this tenant.</Typography>
                )}
                 {isError && (
                    <Typography color="error" sx={{p: 3, textAlign: 'center'}}>Could not load programs. Please try again later.</Typography>
                 )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ProgramInviteGeneratorModal;